"use client"

import { Link } from "react-router-dom"
import { useState } from "react"
import "./HomePage.css"
import PlayStoreIcon from "../img/playstore.png"
import AppStoreIcon from "../img/appstore.png"
import PromoBanner from "../img/yupluck-promo-banner.png"
import Logo from "../img/logo3.png"
import AppScreen1 from "../img/yupluck app.png"
import AppScreen2 from "../img/yupluck app 2.png"
import AppScreen3 from "../img/yupluck app 3.png"

const FEATURES = [
  {
    icon: "👥",
    color: "icon-green",
    title: "Member Management",
    desc: "Maintain a complete digital record of every gym member — contact info, membership status, join date, and payment history in one place.",
  },
  {
    icon: "🏋️",
    color: "icon-blue",
    title: "Equipment Tracking",
    desc: "Add, update, and categorise all your gym equipment. Keep track of what you have and make your facility stand out to members browsing online.",
  },
  {
    icon: "🗓️",
    color: "icon-purple",
    title: "Time Slot Scheduling",
    desc: "Create flexible time slots for classes, personal training, or open gym hours. Members can see availability and book sessions seamlessly.",
  },
  {
    icon: "📅",
    color: "icon-orange",
    title: "Booking System",
    desc: "Manage reservations from members with an easy-to-use admin booking panel. View, approve, or update bookings in real time.",
  },
  {
    icon: "💳",
    color: "icon-cyan",
    title: "Payment Tracking",
    desc: "Track every payment, outstanding fee, and subscription renewal. See your daily and monthly revenue at a glance on a clean sales dashboard.",
  },
  {
    icon: "📊",
    color: "icon-indigo",
    title: "Analytics Dashboard",
    desc: "Visualise revenue trends, booking counts, and member growth with interactive line charts. Make data-driven decisions about your gym.",
  },
  {
    icon: "🖼️",
    color: "icon-pink",
    title: "Photo Gallery",
    desc: "Upload high-quality images of your gym, equipment, and classes. A great gallery convinces new members to walk through your doors.",
  },
  {
    icon: "📱",
    color: "icon-teal",
    title: "QR Code Check-ins",
    desc: "Generate unique QR codes for each member. Scan at the entrance for instant, contactless check-in — no paper registers needed.",
  },
  {
    icon: "🏦",
    color: "icon-yellow",
    title: "Bank & Payout Setup",
    desc: "Link your bank account once and receive direct payouts. Transparent commission structure so you always know what you earn.",
  },
  {
    icon: "🔔",
    color: "icon-red",
    title: "Push Notifications",
    desc: "Send instant announcements about new classes, offers, or schedule changes to all your members on their phones.",
  },
  {
    icon: "📍",
    color: "icon-lime",
    title: "Location & Discovery",
    desc: "Pin your gym on the map with Google Places integration. Members nearby discover your gym first when searching the Yupluck app.",
  },
  {
    icon: "🛡️",
    color: "icon-sky",
    title: "Secure Admin Panel",
    desc: "Role-based admin access keeps your business data protected. Full audit trail on bookings and payments for complete accountability.",
  },
]

const HOW_IT_WORKS = [
  {
    num: "01",
    title: "Create Your Free Account",
    desc: "Register your gym in under 5 minutes. Fill in your gym name, location, and a short description — no credit card required.",
  },
  {
    num: "02",
    title: "Set Up Your Profile",
    desc: "Add your equipment, create time slots, upload gallery photos, and link your bank account using the guided onboarding checklist.",
  },
  {
    num: "03",
    title: "Go Live & Get Discovered",
    desc: "Your gym appears on the Yupluck app instantly. Fitness seekers nearby can find, view, and book sessions at your gym.",
  },
  {
    num: "04",
    title: "Manage & Grow",
    desc: "Use the dashboard to track bookings, monitor revenue, manage members, and send notifications — all from one place.",
  },
]

const APP_SCREENS = [
  { img: AppScreen1, label: "Fitness Feed",      desc: "Members share workouts & progress" },
  { img: AppScreen2, label: "Challenges",         desc: "Join & create fitness challenges" },
  { img: AppScreen3, label: "Discover Gyms",      desc: "Your gym listed & bookable here" },
]

const BENEFITS = [
  { icon: "🆓", title: "Completely Free", desc: "List your gym, manage members, and track payments with zero subscription fee." },
  { icon: "⚡", title: "Set Up in Minutes", desc: "Guided onboarding gets your gym live in under 10 minutes — no tech skills needed." },
  { icon: "🌍", title: "Growing Community", desc: "Join early and get featured on the Yupluck app as we grow our community of fitness seekers across India." },
  { icon: "📲", title: "Mobile-First", desc: "Manage your gym on the go from our app, available on iOS and Android." },
  { icon: "🔒", title: "Secure & Reliable", desc: "Bank-level security for all payments and member data. Always on, always fast." },
  { icon: "🤝", title: "Dedicated Support", desc: "Our team is always ready to help you get the most out of the platform." },
]

const Home = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  return (
    <div className="yupluck-homepage">

      {/* ── HEADER ─────────────────────────────── */}
      <header className="hp-header">
        <div className="hp-header-inner">
          <Link to="/" className="hp-logo">
            <img src={Logo} alt="Yupluck" />
            <span>Yupluck</span>
          </Link>

          <nav className="hp-nav">
            <a href="#features">Features</a>
            <a href="#how-it-works">How It Works</a>
            <Link to="/about">About</Link>
          </nav>

          <div className="hp-actions">
            <Link to="/login" className="btn-ghost">Sign In</Link>
            <Link to="/register" className="btn-green">List Your Gym Free →</Link>
          </div>

          <button
            className="hp-hamburger"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Open menu"
          >
            <span></span><span></span><span></span>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="hp-mobile-menu">
            <a href="#features" onClick={() => setIsMobileMenuOpen(false)}>Features</a>
            <a href="#how-it-works" onClick={() => setIsMobileMenuOpen(false)}>How It Works</a>
            <Link to="/about" onClick={() => setIsMobileMenuOpen(false)}>About</Link>
            <Link to="/login" onClick={() => setIsMobileMenuOpen(false)}>Sign In</Link>
            <Link to="/register" onClick={() => setIsMobileMenuOpen(false)}>List Your Gym Free →</Link>
          </div>
        )}
      </header>

      {/* ── HERO ───────────────────────────────── */}
      <section className="hp-hero">
        <div className="hp-hero-content">
          <div className="hp-hero-badge">
            <span className="hp-hero-badge-dot"></span>
            Free for all gym owners
          </div>

          <h1>
            The Smarter Way to<br />
            <span className="gradient-text">Run Your Gym</span>
          </h1>

          <p className="hp-hero-sub">
            Yupluck gives gym owners powerful tools to manage members, schedule classes,
            track payments, and grow their business — completely free, forever.
          </p>

          <div className="hp-hero-cta">
            <Link to="/register" className="btn-hero-primary">
              Get Started Free →
            </Link>
            <a href="#features" className="btn-hero-secondary">
              See All Features
            </a>
          </div>

          <p className="hp-hero-trust">
            Built for gym owners across India &nbsp;·&nbsp; <span>Free to list, free forever</span>
          </p>
        </div>
      </section>

      {/* ── STATS ──────────────────────────────── */}
      <div className="hp-stats">
        <div className="hp-stats-inner">
          <div className="hp-stat">
            <div className="hp-stat-number">₹0</div>
            <div className="hp-stat-label">Listing Cost</div>
          </div>
          <div className="hp-stat">
            <div className="hp-stat-number">12</div>
            <div className="hp-stat-label">Powerful Features</div>
          </div>
          <div className="hp-stat">
            <div className="hp-stat-number">&lt;5 min</div>
            <div className="hp-stat-label">Setup Time</div>
          </div>
          <div className="hp-stat">
            <div className="hp-stat-number">India</div>
            <div className="hp-stat-label">Built For</div>
          </div>
          <div className="hp-stat">
            <div className="hp-stat-number">Free</div>
            <div className="hp-stat-label">Forever</div>
          </div>
        </div>
      </div>

      {/* ── FEATURES ────────────────────────────── */}
      <section className="hp-section hp-features" id="features">
        <div className="hp-section-inner">
          <div className="hp-features-header">
            <div className="hp-section-tag">Everything You Need</div>
            <h2 className="hp-section-title">
              12 Features. <span className="accent">One Platform.</span>
            </h2>
            <p className="hp-section-desc">
              From member check-ins to revenue analytics — every tool your gym needs is
              built in and ready to use the moment you sign up.
            </p>
          </div>

          <div className="hp-features-grid">
            {FEATURES.map((f, i) => (
              <div className="hp-feature-card" key={i}>
                <div className={`hp-feature-icon ${f.color}`}>{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ───────────────────────── */}
      <section className="hp-how" id="how-it-works">
        <div className="hp-section-inner">
          <div className="hp-how-layout">
            <div className="hp-how-left">
              <div className="hp-section-tag">Simple Process</div>
              <h2 className="hp-section-title">
                Up and running<br /><span className="accent">in 4 steps</span>
              </h2>
              <p className="hp-section-desc">
                No technical knowledge needed. Our guided onboarding walks you through
                everything step by step.
              </p>
            </div>

            <div className="hp-how-steps">
              {HOW_IT_WORKS.map((step, i) => (
                <div className="hp-how-step" key={i}>
                  <div className="hp-how-step-left">
                    <div className="hp-step-num">{step.num}</div>
                    <div className="hp-step-line"></div>
                  </div>
                  <div className="hp-how-step-content">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── APP SCREENSHOTS ────────────────────── */}
      <section className="hp-screens">
        <div className="hp-section-inner">
          <div className="hp-screens-header">
            <div className="hp-section-tag">Member App</div>
            <h2 className="hp-section-title">
              A Beautiful App <span className="accent">Your Members Will Love</span>
            </h2>
            <p className="hp-section-desc" style={{ margin: "0 auto" }}>
              When you list on Yupluck, your gym is instantly discoverable inside our member app.
              Members browse, book sessions, join challenges, and track their fitness journey.
            </p>
          </div>

          {/* Three-phone showcase */}
          <div className="hp-phones-row">
            <div className="hp-phone-wrap side">
              <div className="hp-phone-frame">
                <div className="hp-phone-island"></div>
                <div className="hp-phone-screen">
                  <img src={AppScreen1} alt="Fitness Feed" />
                </div>
              </div>
              <div className="hp-screen-label">Fitness Feed</div>
              <div className="hp-screen-desc">Members share workouts &amp; progress</div>
            </div>

            <div className="hp-phone-wrap center">
              <div className="hp-phone-frame">
                <div className="hp-phone-island"></div>
                <div className="hp-phone-screen">
                  <img src={AppScreen2} alt="Discover Gyms" />
                </div>
              </div>
              <div className="hp-screen-label">Discover Gyms</div>
              <div className="hp-screen-desc">Your gym listed &amp; bookable here</div>
            </div>

            <div className="hp-phone-wrap side">
              <div className="hp-phone-frame">
                <div className="hp-phone-island"></div>
                <div className="hp-phone-screen">
                  <img src={AppScreen3} alt="Challenges" />
                </div>
              </div>
              <div className="hp-screen-label">Challenges</div>
              <div className="hp-screen-desc">Join &amp; create fitness challenges</div>
            </div>
          </div>

          {/* Feature cards */}
          <div className="hp-screen-features">
            <div className="hp-phone-feature-card">
              <div className="hp-pf-icon">📱</div>
              <h3>Feed &amp; Community</h3>
              <p>Members share fitness posts, progress photos, and workout updates with each other.</p>
            </div>
            <div className="hp-phone-feature-card">
              <div className="hp-pf-icon">🏋️</div>
              <h3>Gym Discovery</h3>
              <p>Your gym appears on the map with photos, pricing, slots, and a Book Now button.</p>
            </div>
            <div className="hp-phone-feature-card">
              <div className="hp-pf-icon">🏆</div>
              <h3>Fitness Challenges</h3>
              <p>Create or join challenges. Members stay motivated and your gym stays top-of-mind.</p>
            </div>
            <div className="hp-phone-feature-card">
              <div className="hp-pf-icon">🎬</div>
              <h3>Reels &amp; Videos</h3>
              <p>Short fitness reels let new members discover your gym organically through content.</p>
            </div>
          </div>

          <div className="hp-screens-stores">
            <p>Available free for your members on</p>
            <div className="hp-app-stores">
              <a
                href="https://play.google.com/store/apps/details?id=com.yupluck.mepluck"
                target="_blank"
                rel="noopener noreferrer"
                className="hp-store-btn"
              >
                <img src={PlayStoreIcon} alt="Google Play" />
                Google Play
              </a>
              <a
                href="https://apps.apple.com/in/app/yupluck/id6737851845"
                target="_blank"
                rel="noopener noreferrer"
                className="hp-store-btn"
              >
                <img src={AppStoreIcon} alt="App Store" />
                App Store
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── APP PROMO ──────────────────────────── */}
      <section className="hp-app">
        <div className="hp-section-inner">
          <div className="hp-app-layout">
            <div className="hp-app-img">
              <img src={PromoBanner} alt="Yupluck App Preview" />
            </div>
            <div className="hp-app-text">
              <div className="hp-section-tag">Mobile App</div>
              <h2>Your Gym in Every<br />Member's Pocket</h2>
              <p>
                The Yupluck member app lets your users discover your gym, book sessions,
                scan QR codes, and track their fitness journey — all from their phone.
                Download the app and see it from your members' perspective.
              </p>
              <div className="hp-app-stores">
                <a
                  href="https://play.google.com/store/apps/details?id=com.yupluck.mepluck"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hp-store-btn"
                >
                  <img src={PlayStoreIcon} alt="Google Play" />
                  Google Play
                </a>
                <a
                  href="https://apps.apple.com/in/app/yupluck/id6737851845"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hp-store-btn"
                >
                  <img src={AppStoreIcon} alt="App Store" />
                  App Store
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ───────────────────────────── */}
      <section className="hp-section hp-benefits">
        <div className="hp-section-inner">
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <div className="hp-section-tag">Why Yupluck</div>
            <h2 className="hp-section-title">
              Built for <span className="accent">Gym Owners</span>,<br />Not Developers
            </h2>
          </div>
          <div className="hp-benefits-grid">
            {BENEFITS.map((b, i) => (
              <div className="hp-benefit-card" key={i}>
                <div className="hp-benefit-icon">{b.icon}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────── */}
      <section className="hp-cta">
        <div className="hp-cta-inner">
          <div className="hp-cta-box">
            <div className="hp-section-tag" style={{ display: "inline-block" }}>Get Started Today</div>
            <h2>List Your Gym.<br />Grow Your Business.</h2>
            <p>
              Join gym owners already using Yupluck to reach more members,
              streamline operations, and earn more revenue — for free.
            </p>
            <div className="hp-cta-buttons">
              <Link to="/register" className="btn-hero-primary">
                Register Your Gym Free →
              </Link>
              <Link to="/login" className="btn-hero-secondary">
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ─────────────────────────────── */}
      <footer className="hp-footer">
        <div className="hp-footer-inner">
          <div className="hp-footer-top">
            <div className="hp-footer-brand">
              <img src={Logo} alt="Yupluck" />
              <p>
                The complete gym management platform for modern fitness businesses.
                Free to list. Powerful to use.
              </p>
              <div className="hp-footer-social">
                <a href="#!" aria-label="Instagram">📷</a>
                <a href="#!" aria-label="Twitter">🐦</a>
                <a href="#!" aria-label="Facebook">📘</a>
              </div>
            </div>

            <div className="hp-footer-col">
              <h4>Platform</h4>
              <a href="#features">Features</a>
              <a href="#how-it-works">How It Works</a>
              <Link to="/about">About</Link>
              <Link to="/contact">Contact</Link>
            </div>

            <div className="hp-footer-col">
              <h4>Account</h4>
              <Link to="/login">Sign In</Link>
              <Link to="/register">List Your Gym</Link>
              <Link to="/login">Forgot Password</Link>
            </div>

            <div className="hp-footer-col">
              <h4>Legal</h4>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/terms">Terms of Service</Link>
            </div>
          </div>

          <div className="hp-footer-bottom">
            <p>© {new Date().getFullYear()} Yupluck. All rights reserved.</p>
            <p>
              Made with ❤️ in India &nbsp;·&nbsp;{" "}
              <a href="mailto:contact@yupluck.com">contact@yupluck.com</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default Home
